import '../../billing-management/style.scss';
import {
  getBillMonthCost,
  getBillList,
  fetchGameList,
} from '../../../../../api';
import { useEffect, useState } from 'react';
import {
  IBillMonthCost,
  IBillList,
} from '../../../../../model/billing-center/balance';
import { getPrice } from '../../../../../lib';
import getFormatMessage from '../../../../../locales/get-format-message';
import { RecentYearsChartComponent } from './recent-years-chart';
import moment from 'moment';
import { BillChartPie } from '../../../../../components/chart/bill-pie';
import { getServiceList } from '../../../../../api/services';
import {
  IAPIServiceConfigItem,
  IAPIGameItem,
} from '../../../../../model/api-modules';
import { PRODUCT_COLOR_LIST } from '../../../../../components/chart/pie-color';
import emptyImg from '../../../../../assets/img/empty.png';

interface IProps {
  readonly studioId: string;
  readonly dateRange: string;
}

export const BillingManagementOverview = (props: IProps) => {
  const { studioId, dateRange } = props;
  // const [loading, setLoading] = useState<boolean>(false);
  const [billMonthCost, setBillMonthCost] = useState<IBillMonthCost | null>(
    null
  );
  const [serviceConfig, setServiceConfig] = useState<
    IAPIServiceConfigItem[] | null
  >(null);
  const [gameList, setGameList] = useState<ReadonlyArray<IAPIGameItem>>([]);
  const [serviceData, setServiceData] = useState<
    readonly { name: string; value: number; color?: string }[][]
  >([[]]);
  const [gameData, setGameData] = useState<
    readonly { name: string; value: number }[][]
  >([[]]);
  const [modeData, setModeData] = useState<
    readonly { name: string; value: number; services?: string[] }[][]
  >([[]]);
  const [billList, setBillList] = useState<IBillList[]>([]);

  useEffect(() => {
    getServiceConfigList();
    getGameList();
    getWithDrawaList();
  }, []);

  useEffect(() => {
    getBillMonthCostData();
    getConsumeCostData();
  }, [dateRange, billList, gameList]);

  const getWithDrawaList = async () => {
    const res = await getBillList(studioId, 1, 99999);
    const items = res?.list;
    if (items) {
      setBillList(items);
    }
  };

  const getServiceConfigList = async () => {
    const res = await getServiceList();
    if (res) {
      setServiceConfig(res);
    }
  };

  const getGameList = async () => {
    const gameRes = await fetchGameList();
    setGameList(gameRes || []);
  };

  // 颜色分配函数
  const getServiceColors = (serviceResult: any[]) => {
    if (!serviceConfig) {
      return;
    }
    const usedColors: { [key: string]: boolean[] } = {}; // 用于追踪每个key下的颜色是否已经使用
    const colorAssignments: any[] = [];

    // 创建一个 map 方便通过 ParentName 获取对应的颜色数组
    const productColorMap: { [key: string]: string[] } = {};

    PRODUCT_COLOR_LIST.forEach((colorData) => {
      productColorMap[colorData.key] = colorData.value;
    });
    // 遍历 serviceResult 为每个服务分配颜色
    serviceResult.forEach((service) => {
      const serviceConfigItem = serviceConfig.find(
        (config) => config.Name === service.name
      );

      if (serviceConfigItem) {
        const parentKey = serviceConfigItem.ParentName;
        const availableColors = productColorMap[parentKey] || [];

        // 查找一个未使用的颜色
        for (let i = 0; i < availableColors.length; i++) {
          if (!usedColors[parentKey]?.[i]) {
            usedColors[parentKey] = usedColors[parentKey] || [];
            usedColors[parentKey][i] = true;

            colorAssignments.push({
              name: service.name,
              color: availableColors[i],
              value: service.value,
            });
            break;
          }
        }
      }
    });

    return colorAssignments;
  };

  const getConsumeCostData = () => {
    if (billList.length !== 0) {
      if (dateRange) {
        const filteredData = billList.filter((item) => {
          const month = moment(item.bill_end_date).format('YYYY-MM');
          return month === dateRange;
        });
        const serviceAggregated: any = {};
        const gameAggregated: any = {};
        const orderServiceAggregated: any = {};
        const modeOne: string[] = []; // 暂时只有按量计费订单
        filteredData.forEach((item) => {
          const serviceItem = serviceConfig?.find(
            (it) => it.id === item.service_id
          );
          // const orderServiceItem = serviceConfig?.find(
          //   (it) => it.id === item.order.service_id
          // );
          const gameItem = gameList?.find(
            (it) => it.gameID === item.game_id?.toString()
          );
          if (serviceItem) {
            if (serviceAggregated[serviceItem.Name]) {
              serviceAggregated[serviceItem.Name] += parseFloat(
                item.total_cost
              );
            } else {
              serviceAggregated[serviceItem.Name] = parseFloat(item.total_cost);
            }
          }
          if (item.order?.order_mode === 1) {
            if (orderServiceAggregated.mode_one) {
              orderServiceAggregated.mode_one += parseFloat(item.total_cost);
            } else {
              orderServiceAggregated.mode_one = parseFloat(item.total_cost);
            }
            if (!modeOne.find((it) => it === item.order.service_name)) {
              modeOne.push(item.order.service_name);
            }
          }
          if (gameItem) {
            if (gameAggregated[gameItem.gameName]) {
              gameAggregated[gameItem.gameName] += parseFloat(item.total_cost);
            } else {
              gameAggregated[gameItem.gameName] = parseFloat(item.total_cost);
            }
          }
        });
        const serviceResult: any = Object.entries(serviceAggregated)
          .map(([name, value]) => ({
            name,
            value,
          }))
          .sort((a: any, b: any) => b.value - a.value);
        const gameResult: any = Object.entries(gameAggregated)
          .map(([name, value]) => ({
            name,
            value,
          }))
          .sort((a: any, b: any) => b.value - a.value);
        const orderServiceResult: any = Object.entries(orderServiceAggregated)
          .map(([name, value]) => ({
            name,
            value,
            services: name === 'mode_one' ? modeOne : [], // 暂时只有按量计费订单，后续增加枚举再修改
          }))
          .sort((a: any, b: any) => b.value - a.value);
        const colorServiceResult =
          getServiceColors(serviceResult) || serviceResult;
        setServiceData([colorServiceResult]);
        setGameData([gameResult]);
        setModeData([orderServiceResult]);
      } else {
        setServiceData([[]]);
        setGameData([[]]);
        setModeData([[]]);
      }
    }
  };

  const getBillMonthCostData = async () => {
    const res = await getBillMonthCost(studioId, dateRange);
    if (res) {
      setBillMonthCost(res);
    } else {
      setBillMonthCost(null);
    }
  };

  const renderEmpty = () => {
    return (
      <div style={{ marginTop: 36 }}>
        <img src={emptyImg} style={{ display: 'block', margin: '0 auto' }} />
        <div
          style={{
            textAlign: 'center',
            fontSize: 14,
            color: '#86909C',
            lineHeight: '22px',
          }}
        >
          {getFormatMessage('no_data_available')}
        </div>
      </div>
    );
  };

  const renderServicePie = () => {
    if (serviceData[0].length === 0) {
      return renderEmpty();
    }
    return (
      <BillChartPie
        title={`${serviceData[0].length || ''}`}
        subTitle={getFormatMessage('products')}
        style={{ width: '100%', height: 270 }}
        seriesData={serviceData}
        isProduct={true}
        menuName={getFormatMessage('bill_management_product_name')}
      />
    );
  };

  const renderGamePie = () => {
    if (gameData[0].length === 0) {
      return renderEmpty();
    }
    return (
      <BillChartPie
        title={`${gameData[0].length || ''}`}
        subTitle={getFormatMessage('bill_management_game_project')}
        style={{ width: '100%', height: 300 }}
        seriesData={gameData}
        menuName={getFormatMessage('bill_management_game_name')}
      />
    );
  };

  const renderModePie = () => {
    if (modeData[0].length === 0) {
      return renderEmpty();
    }
    return (
      <BillChartPie
        title={`${modeData[0].length || ''}`}
        subTitle={getFormatMessage('bill_management_billing_model')}
        style={{ width: '100%', minHeight: 300 }}
        seriesData={modeData}
        styleType={1}
      />
    );
  };

  return (
    <div className="billing-management-overview">
      <div className="overview-top">
        <div className="overview-top-month-cost">
          <div className="overview-top-month-cost-title">
            {getFormatMessage('bill_management_month_cost')}
          </div>
          <div className="overview-top-month-cost-num">
            {getPrice(billMonthCost?.amount || '').text}
          </div>
          <div className="overview-top-month-cost-date-box">
            <span className="overview-top-month-cost-date">{`${getFormatMessage(
              'bill_management_statistical_cycle'
            )}: ${billMonthCost?.start_date || '- -'} ${getFormatMessage(
              'bill_management_to'
            )} ${billMonthCost?.end_date || '- -'}`}</span>
            <span className="overview-top-month-cost-billed">
              {billMonthCost?.billed
                ? getFormatMessage('bill_management_paid')
                : getFormatMessage('bill_management_not_paid')}
            </span>
          </div>
        </div>
        <RecentYearsChartComponent
          recentMonthsList={billList}
          dateRange={dateRange}
        />
      </div>
      <div className="overview-center">
        <div className="overview-center-item">
          <div className="overview-center-item-title">
            {getFormatMessage('bill_management_product_based')}
          </div>
          {renderServicePie()}
        </div>
        <div className="overview-center-item">
          <div className="overview-center-item-title">
            {getFormatMessage('bill_management_game_based')}
          </div>
          {renderGamePie()}
        </div>
      </div>
      <div className="overview-bottom">
        <div className="overview-center-item">
          <div className="overview-center-item-title">
            {getFormatMessage('bill_management_billing_based')}
          </div>
          {renderModePie()}
        </div>
      </div>
    </div>
  );
};
